import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Building2, CheckCircle2, Copy, ExternalLink, Loader2, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { fetchGuideWithAuth } from '../utils/authenticatedFetchGuide';
import { getFirebaseAuthToken } from '../utils/firebaseAuthToken';

const API_BASE_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net';
const DELETE_FILE_ENDPOINT = `${API_BASE_URL}/backendDeleteFileFromFirebase`;

const formatDateTime = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
};

const PublishedVenuesListing = ({ onLoadVenue }) => {
  const { currentUser } = useAuth();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingVenueId, setDeletingVenueId] = useState('');
  const [openingVenueId, setOpeningVenueId] = useState('');

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (copyError) {
      console.error(`Failed to copy ${label}:`, copyError);
      toast.error(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  const loadVenues = useCallback(async () => {
    if (!currentUser) {
      setVenues([]);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = await currentUser.getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/listPublishedVenues`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to fetch venues (${response.status})`);
      }

      setVenues(Array.isArray(data.venues) ? data.venues : []);
    } catch (fetchError) {
      console.error('Failed to load published venues:', fetchError);
      setError(fetchError.message || 'Failed to load published venues');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const handleDeleteVenue = useCallback(async (venue) => {
    if (!venue?.jsonUrl || !venue?.storagePath) {
      toast.error('Venue file is missing a storage URL.');
      return;
    }

    const venueName = venue.displayName || venue.id || 'this venue';
    const confirmed = window.confirm(
      `Unpublish "${venueName}"?\n\nThis will delete the venue page JSON from Firebase Storage.`
    );

    if (!confirmed) return;

    try {
      setDeletingVenueId(venue.storagePath || venue.id || '');
      const token = await getFirebaseAuthToken(currentUser);
      const response = await fetch(DELETE_FILE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          fileUrl: venue.jsonUrl
        })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.error || `Failed to delete venue (${response.status})`);
      }

      setVenues((prev) => prev.filter((item) => item.storagePath !== venue.storagePath));
      toast.success(`Unpublished "${venueName}"`);
    } catch (deleteError) {
      console.error('Failed to delete venue:', deleteError);
      toast.error(deleteError.message || 'Failed to unpublish venue');
    } finally {
      setDeletingVenueId('');
    }
  }, [currentUser]);

  const handleOpenVenue = useCallback(async (venue) => {
    if (!onLoadVenue || !venue?.jsonUrl) {
      return;
    }

    try {
      setOpeningVenueId(venue.storagePath || venue.id || '');
      const response = await fetchGuideWithAuth(currentUser, venue.jsonUrl);
      if (!response.ok) {
        throw new Error(`Failed to load venue JSON: ${response.status}`);
      }

      const fullVenueData = await response.json();
      onLoadVenue(fullVenueData, venue.jsonUrl);
    } catch (openError) {
      console.error('Failed to open venue:', openError);
      toast.error(openError.message || 'Failed to open venue');
    } finally {
      setOpeningVenueId('');
    }
  }, [currentUser, onLoadVenue]);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  const filteredVenues = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return venues;

    return venues.filter((venue) =>
      [
        venue.displayName,
        venue.id,
        venue.entityType,
        venue.qrCodeId
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [venues, searchTerm]);

  const getVenueHealth = (venue) => {
    const warnings = Array.isArray(venue?.warnings) ? venue.warnings : [];
    if (warnings.length === 0) {
      return {
        label: 'Ready',
        className: 'bg-emerald-100 text-emerald-800',
        icon: <CheckCircle2 className="w-3.5 h-3.5" />
      };
    }

    return {
      label: `${warnings.length} warning${warnings.length === 1 ? '' : 's'}`,
      className: 'bg-amber-100 text-amber-800',
      icon: <AlertTriangle className="w-3.5 h-3.5" />
    };
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-emerald-100 p-2 text-emerald-700">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold">Published Venues ({filteredVenues.length})</h3>
            <p className="text-sm text-gray-500">Published venue landing pages from Firebase Storage.</p>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search venues"
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-full sm:w-64"
            />
          </div>
          <button
            onClick={loadVenues}
            disabled={loading}
            className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Refreshing...' : 'Reload'}
          </button>
        </div>
      </div>

      {loading && venues.length === 0 ? (
        <div className="flex items-center justify-center py-10 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading venues...
        </div>
      ) : null}

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-600">{error}</p>
        </div>
      ) : null}

      {!loading && !error && filteredVenues.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          {venues.length === 0 ? 'No published venues found.' : 'No venues match your search.'}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {filteredVenues.map((venue) => (
          <div key={venue.storagePath || venue.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            {(() => {
              const venueHealth = getVenueHealth(venue);
              return (
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="text-lg font-semibold text-gray-900">{venue.displayName || venue.id}</h4>
                <div className="mt-1 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-800">
                    {venue.entityType || 'venue'}
                  </span>
                  <span className="rounded-full bg-sky-100 px-2 py-1 text-sky-800">
                    QR ID: {venue.qrCodeId || venue.id}
                  </span>
                  <span className="rounded-full bg-gray-200 px-2 py-1 text-gray-700">
                    {venue.guideCount || 0} guides
                  </span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 ${venueHealth.className}`}>
                    {venueHealth.icon}
                    {venueHealth.label}
                  </span>
                </div>
              </div>
            </div>
              );
            })()}

            <div className="mt-4 space-y-2 text-sm text-gray-600">
              <p>
                <span className="font-medium text-gray-800">Updated:</span> {formatDateTime(venue.updatedAt || venue.createdAt)}
              </p>
              <p>
                <span className="font-medium text-gray-800">Guide links:</span> {venue.guideCount || 0}
                {typeof venue.rawGuideCount === 'number' && venue.rawGuideCount !== venue.guideCount ? ` (${venue.rawGuideCount} raw entries)` : ''}
              </p>
              <p className="break-all">
                <span className="font-medium text-gray-800">Storage:</span> {venue.storagePath}
              </p>
              {Array.isArray(venue.warnings) && venue.warnings.length > 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
                  <p className="font-medium">Needs attention</p>
                  <p className="mt-1 text-sm">{venue.warnings.join(' • ')}</p>
                </div>
              ) : null}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => handleOpenVenue(venue)}
                disabled={openingVenueId === (venue.storagePath || venue.id)}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                {openingVenueId === (venue.storagePath || venue.id) ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                Open in Venue Page
              </button>
              <button
                onClick={() => copyText(venue.qrUrl, 'QR URL')}
                className="px-3 py-1.5 bg-white border border-gray-300 text-sm rounded-lg hover:bg-gray-100 flex items-center gap-1.5"
              >
                <Copy className="w-4 h-4" />
                Copy QR URL
              </button>
              <button
                onClick={() => copyText(venue.jsonUrl, 'JSON URL')}
                className="px-3 py-1.5 bg-white border border-gray-300 text-sm rounded-lg hover:bg-gray-100 flex items-center gap-1.5"
              >
                <Copy className="w-4 h-4" />
                Copy JSON URL
              </button>
              <a
                href={venue.qrUrl}
                target="_blank"
                rel="noreferrer"
                aria-disabled={!venue.qrUrl}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 inline-flex items-center gap-1.5"
              >
                <ExternalLink className="w-4 h-4" />
                Open QR Page
              </a>
              <button
                onClick={() => handleDeleteVenue(venue)}
                disabled={deletingVenueId === (venue.storagePath || venue.id)}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                {deletingVenueId === (venue.storagePath || venue.id) ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Unpublish
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PublishedVenuesListing;
